import React from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle } from 'lucide-react';

// ============================================================================
// AdminActivationWarningModal — global_admin safety confirmation before enabling
// AI on a campaign.
//
// This replaces a native window.confirm() and nothing else: the warning copy and
// the surrounding toggle logic are unchanged, only the delivery. Cancel does
// exactly what dismissing the old dialog did (nothing happens); Confirm resumes
// the same toggle path the confirm() used to fall through to.
//
// Structure follows the platform's existing warning modals (centered card, icon
// circle, rounded-2xl) so it sits in the family rather than looking bolted on.
// ============================================================================

export default function AdminActivationWarningModal({ campaignName, onConfirm, onCancel }) {
  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center font-sans antialiased">
      <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative mx-4 w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        {/* Icon */}
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
          <AlertTriangle className="h-6 w-6 text-red-600" />
        </div>

        {/* Content — copy preserved verbatim from the confirm() this replaces. */}
        <div className="mb-6 text-center">
          <h3 className="mb-2 text-xl font-bold text-gray-900">
            Global Admin Warning
          </h3>
          <p className="mb-4 text-gray-600">
            You are about to enable AI on &ldquo;<span className="font-semibold text-gray-800">{campaignName}</span>&rdquo;.
          </p>
          <p className="text-gray-600">
            This will activate live AI messaging for this campaign&rsquo;s leads. Are you sure?
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <button
            onClick={onConfirm}
            className="w-full rounded-2xl bg-red-600 px-4 py-2.5 font-semibold text-white shadow transition-all hover:bg-red-700"
          >
            Confirm
          </button>
          <button
            onClick={onCancel}
            className="w-full rounded-2xl bg-gray-100 px-4 py-2 font-medium text-gray-700 shadow transition-all hover:bg-gray-200"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
